import { useState } from 'react'
import { CheckCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useCompleteNoteSession } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'

interface CompleteSessionButtonProps {
  sessionId: string
  hasUnsavedChanges?: boolean
  /** Returns the current editor content so completion persists it atomically. */
  getContent?: () => { notes_content: string; notes_plain_text: string } | undefined
  onComplete?: () => void
  disabled?: boolean
}

export function CompleteSessionButton({
  sessionId,
  hasUnsavedChanges = false,
  getContent,
  onComplete,
  disabled,
}: CompleteSessionButtonProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const completeMutation = useCompleteNoteSession()
  const { toast } = useToast()

  const handleClick = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true)
    } else {
      handleComplete()
    }
  }

  const handleComplete = () => {
    // Send the current content with completion so it's saved atomically — finalizing
    // never depends on a separate autosave having landed.
    const content = getContent?.()
    completeMutation.mutate(
      { id: sessionId, ...(content ?? {}) },
      {
        onSuccess: () => {
          toast({
            title: 'Session completed',
            description: 'Your notes have been finalized and attached to the entity.',
          })
          onComplete?.()
        },
        onError: (error) => {
          toast({
            title: 'Failed to complete session',
            description: explainError(error, 'An error occurred'),
            variant: 'destructive',
          })
        },
      }
    )
  }

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled || completeMutation.isPending}
        variant="default"
        aria-label="Complete note session"
      >
        {completeMutation.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <CheckCircle className="mr-2 h-4 w-4" aria-hidden="true" />
        )}
        Complete Session
      </Button>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete session with unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes that may not be included. Are you sure you want to complete
              this session?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleComplete}>Complete Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
